// Sticky Add to Cart v2.2 — bundle-aware
// - Variant-aware (price + image update on variant change)
// - Bundle-aware: listens for fb-bundle:change. When bundle is active, displays the
//   bundle's price/qty/image and on add, relays the click to the main product CTA so
//   the bundle's hijack handler does the actual cart add (with discount code).
// - Otherwise: direct AJAX add of the sticky's own variant + qty.
// - Dispatches cart:update so Horizon's cart drawer opens.

if (!customElements.get('sticky-add-to-cart')) {
  class StickyAddToCart extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.productId = this.dataset.productId;
      this.showOnlyOnScroll = this.classList.contains('sticky-add-to-cart--show-on-scroll');

      this.mainAddToCartForm = this.findMainForm();
      this.mainAddToCartButton = this.mainAddToCartForm ? this.mainAddToCartForm.querySelector('[type="submit"]') : null;

      this.stickyForm = this.querySelector('form');
      this.stickyVariantInput = this.querySelector('[name="id"]');
      this.quantityInput = this.querySelector('.sticky-qty__input');
      this.quantityButtons = this.querySelectorAll('.sticky-qty__btn');
      this.qtyWrap = this.querySelector('.sticky-add-to-cart__quantity');
      this.addToCartButton = this.querySelector('.sticky-add-to-cart__cta');
      this.ctaLabel = this.querySelector('.sticky-add-to-cart__cta-label');
      this.priceEl = this.querySelector('[data-sticky-price]');
      this.compareEl = this.querySelector('[data-sticky-compare]');
      this.imageEl = this.querySelector('[data-sticky-image]');
      this.imageWrap = this.querySelector('[data-sticky-image-wrap]');
      this.titleEl = this.querySelector('[data-sticky-title]');

      // Variant data from JSON
      this.variants = {};
      const dataEl = document.getElementById('StickyAddToCartData-' + this.sectionId);
      if (dataEl) {
        try {
          const json = JSON.parse(dataEl.textContent);
          this.variants = json.variants || {};
          this.defaultImageUrl = json.default_image_url;
        } catch (e) {}
      }

      // Snapshot of single-variant defaults for reverting when bundle deselects
      this.singleDefaults = {
        priceText: this.priceEl ? this.priceEl.textContent : '',
        compareText: this.compareEl ? this.compareEl.textContent : '',
        compareHidden: this.compareEl ? this.compareEl.hidden : true,
        imageSrc: this.imageEl ? this.imageEl.src : ''
      };

      this.currentBundleState = null;
      this.isAdding = false;

      this.bindEvents();
      this.init();
    }

    findMainForm() {
      const forms = document.querySelectorAll('form[action*="/cart/add"]');
      for (const f of forms) if (!this.contains(f)) return f;
      return null;
    }

    bindEvents() {
      window.addEventListener('scroll', () => this.onScroll(), { passive: true });

      this.quantityButtons.forEach((button) => {
        button.addEventListener('click', (e) => this.onQuantityButtonClick(e));
      });

      if (this.mainAddToCartForm) {
        this.mainAddToCartForm.addEventListener('change', () => this.syncFromMainForm());
        document.addEventListener('variant:change', () => this.syncFromMainForm());
        document.addEventListener('variantChange', () => this.syncFromMainForm());
      }

      // Listen for bundle state changes
      document.addEventListener('fb-bundle:change', (e) => {
        this.applyBundleState(e.detail);
      });

      // Capture-phase add handlers
      if (this.stickyForm) {
        this.stickyForm.addEventListener('submit', (e) => {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.handleAdd();
        }, true);
      }
      if (this.addToCartButton) {
        this.addToCartButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.handleAdd();
        }, true);
      }

      this.startVariantPolling();
    }

    startVariantPolling() {
      if (!this.mainAddToCartForm) return;
      const mainVariantInput = this.mainAddToCartForm.querySelector('[name="id"]');
      if (!mainVariantInput) return;
      let lastValue = mainVariantInput.value;
      const observer = new MutationObserver(() => {
        if (mainVariantInput.value !== lastValue) {
          lastValue = mainVariantInput.value;
          this.syncFromMainForm();
        }
      });
      observer.observe(mainVariantInput, { attributes: true, attributeFilter: ['value'] });
      setInterval(() => {
        if (mainVariantInput.value !== lastValue) {
          lastValue = mainVariantInput.value;
          this.syncFromMainForm();
        }
      }, 500);
    }

    init() {
      if (this.showOnlyOnScroll) this.checkScrollPosition();
      else this.show();

      // Read bundle state if bundle script ran first
      if (window.__fbBundleState) {
        this.applyBundleState(window.__fbBundleState);
      } else {
        this.syncFromMainForm();
      }

      document.body.classList.add('sticky-add-to-cart-active');
      if (!this.classList.contains('sticky-add-to-cart--mobile-only')) {
        document.body.classList.add('sticky-add-to-cart-desktop');
      }
    }

    onScroll() { if (this.showOnlyOnScroll) this.checkScrollPosition(); }
    checkScrollPosition() {
      if (!this.mainAddToCartButton) { this.show(); return; }
      const r = this.mainAddToCartButton.getBoundingClientRect();
      if (r.bottom < 0) this.show(); else this.hide();
    }
    show() { this.classList.add('is-visible'); }
    hide() { this.classList.remove('is-visible'); }

    onQuantityButtonClick(event) {
      event.preventDefault();
      const button = event.currentTarget;
      const value = parseInt(this.quantityInput.value, 10) || 1;
      const isPlus = button.name === 'plus';
      const qty = isPlus ? value + 1 : Math.max(value - 1, 1);
      this.quantityInput.value = qty;
    }

    syncFromMainForm() {
      // If bundle is active, bundle's state wins
      if (this.currentBundleState) return;
      if (!this.mainAddToCartForm) return;
      const mainVariantInput = this.mainAddToCartForm.querySelector('[name="id"]');
      if (mainVariantInput && this.stickyVariantInput) {
        this.stickyVariantInput.value = mainVariantInput.value;
        this.updateVariantDisplay(mainVariantInput.value);
      }
      const mainButton = this.mainAddToCartForm.querySelector('[type="submit"]');
      if (mainButton && this.addToCartButton) {
        if (mainButton.disabled) {
          this.addToCartButton.disabled = true;
          if (this.ctaLabel) this.ctaLabel.textContent = this.addToCartButton.dataset.soldOutText || 'Sold out';
        } else if (!this.isAdding) {
          this.addToCartButton.disabled = false;
          if (this.ctaLabel) this.ctaLabel.textContent = this.addToCartButton.dataset.addToCartText || 'Add to cart';
        }
      }
    }

    updateVariantDisplay(variantId) {
      if (!variantId) return;
      const variant = this.variants[String(variantId)];
      if (!variant) return;
      if (this.priceEl) this.priceEl.textContent = variant.price_formatted;
      if (this.compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          this.compareEl.textContent = variant.compare_at_price_formatted;
          this.compareEl.hidden = false;
        } else {
          this.compareEl.hidden = true;
        }
      }
      if (this.imageEl && variant.image_url && this.imageEl.src !== variant.image_url) {
        this.imageEl.style.opacity = '0';
        this.imageEl.onload = () => { this.imageEl.style.opacity = '1'; };
        this.imageEl.src = variant.image_url;
      }
      if (this.addToCartButton && !variant.available) {
        this.addToCartButton.disabled = true;
        if (this.ctaLabel) this.ctaLabel.textContent = this.addToCartButton.dataset.soldOutText || 'Sold out';
      }
    }

    applyBundleState(state) {
      this.currentBundleState = state;
      if (state && state.items && state.items.length) {
        // Update price
        if (this.priceEl) this.priceEl.textContent = state.finalFormatted || this.singleDefaults.priceText;
        if (this.compareEl) {
          if (state.compareFormatted && state.compareCents > state.finalCents) {
            this.compareEl.textContent = state.compareFormatted;
            this.compareEl.hidden = false;
          } else {
            this.compareEl.hidden = true;
          }
        }
        // Image swap (use bundle's primary image if provided)
        if (this.imageEl && state.imageUrl && this.imageEl.src !== state.imageUrl) {
          this.imageEl.style.opacity = '0';
          this.imageEl.onload = () => { this.imageEl.style.opacity = '1'; };
          this.imageEl.src = state.imageUrl;
        }
        // Qty badge on image (×2, ×3, etc.)
        this.setQtyBadge(state.qtyTotal > 1 ? state.qtyTotal : 0);
        // Mark as bundle-active so CSS hides the qty stepper
        this.classList.add('is-bundle-active');
      } else {
        // Bundle deselected (rare since one tier is always selected) — revert to single variant
        this.setQtyBadge(0);
        this.classList.remove('is-bundle-active');
        this.syncFromMainForm();
      }
    }

    setQtyBadge(qty) {
      if (!this.imageWrap) return;
      let badge = this.imageWrap.querySelector('.sticky-add-to-cart__qty-badge');
      if (qty > 1) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'sticky-add-to-cart__qty-badge';
          badge.setAttribute('aria-hidden', 'true');
          this.imageWrap.appendChild(badge);
        }
        badge.textContent = '×' + qty;
      } else if (badge) {
        badge.remove();
      }
    }

    getCartSectionIds() {
      const ids = [];
      document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
        const id = el.getAttribute('data-section-id');
        if (id && ids.indexOf(id) === -1) ids.push(id);
      });
      return ids.join(',');
    }

    dispatchCartUpdate(response) {
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: response,
          sourceId: 'sticky-add-to-cart',
          data: { source: 'sticky-add-to-cart', sections: (response && response.sections) ? response.sections : undefined }
        }
      }));
    }

    handleAdd() {
      if (this.isAdding) return;
      if (!this.addToCartButton || this.addToCartButton.disabled) return;

      // If bundle is active, relay the click to the main CTA. The bundle's document-level
      // hijack will catch that click and do the bundle add (with discount code).
      if (this.currentBundleState && this.currentBundleState.items && this.currentBundleState.items.length) {
        this.handleBundleAdd();
        return;
      }

      // Otherwise: direct AJAX add of single variant
      this.handleSingleAdd();
    }

    handleBundleAdd() {
      const mainBtn = this.mainAddToCartForm ? this.mainAddToCartForm.querySelector('[type="submit"]') : null;
      if (!mainBtn) { this.handleSingleAdd(); return; }

      this.isAdding = true;
      this.addToCartButton.classList.add('is-loading');
      if (this.ctaLabel) this.ctaLabel.textContent = 'Adding…';

      // Listen once for the bundle's cart:update so we can flip to "Added" + reset
      const onUpdate = (e) => {
        if (e.detail && e.detail.sourceId === 'fb-upsell-bundle') {
          document.removeEventListener('cart:update', onUpdate);
          this.addToCartButton.classList.remove('is-loading');
          this.addToCartButton.classList.add('is-added');
          if (this.ctaLabel) this.ctaLabel.textContent = 'Added';
          setTimeout(() => {
            this.addToCartButton.classList.remove('is-added');
            if (this.ctaLabel) this.ctaLabel.textContent = this.addToCartButton.dataset.addToCartText || 'Add to cart';
            this.isAdding = false;
          }, 1500);
        }
      };
      document.addEventListener('cart:update', onUpdate);
      // Safety: reset after 6s if no cart:update fires
      setTimeout(() => {
        document.removeEventListener('cart:update', onUpdate);
        if (this.isAdding) {
          this.addToCartButton.classList.remove('is-loading');
          if (this.ctaLabel) this.ctaLabel.textContent = this.addToCartButton.dataset.addToCartText || 'Add to cart';
          this.isAdding = false;
        }
      }, 6000);

      // Relay click to main CTA — bundle's capture-phase hijack will fire
      mainBtn.click();
    }

    handleSingleAdd() {
      const variantId = this.stickyVariantInput ? this.stickyVariantInput.value : null;
      const qty = Math.max(parseInt(this.quantityInput ? this.quantityInput.value : '1', 10) || 1, 1);
      if (!variantId) return;

      this.isAdding = true;
      this.addToCartButton.classList.add('is-loading');
      const originalLabel = this.ctaLabel ? this.ctaLabel.textContent : 'Add to cart';
      if (this.ctaLabel) this.ctaLabel.textContent = 'Adding…';

      const body = { items: [{ id: parseInt(variantId, 10), quantity: qty }] };
      const sections = this.getCartSectionIds();
      if (sections) body.sections = sections;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body)
      }).then((r) => {
        if (!r.ok) throw new Error('cart-add status ' + r.status);
        return r.json();
      }).then((data) => {
        this.addToCartButton.classList.remove('is-loading');
        this.addToCartButton.classList.add('is-added');
        if (this.ctaLabel) this.ctaLabel.textContent = 'Added';
        setTimeout(() => this.dispatchCartUpdate(data), 150);
        setTimeout(() => {
          this.addToCartButton.classList.remove('is-added');
          if (this.ctaLabel) this.ctaLabel.textContent = this.addToCartButton.dataset.addToCartText || originalLabel;
          if (this.quantityInput) this.quantityInput.value = 1;
          this.isAdding = false;
        }, 1500);
      }).catch((err) => {
        console.error('[sticky-add-to-cart] add failed', err);
        this.addToCartButton.classList.remove('is-loading');
        if (this.ctaLabel) this.ctaLabel.textContent = originalLabel;
        this.isAdding = false;
      });
    }
  }

  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
